import { useEffect, useState } from "react";
import { AxiosError } from "axios";

import { useDisputeTimesheet } from "@/features/timesheets/model/useTimesheets";
import type { Timesheet } from "@/features/timesheets/types";
import { Button, Input, Label, Textarea } from "@/shared/ui";

interface Props {
  timesheet: Timesheet;
  onClose: () => void;
}

export function DisputeDialog({ timesheet, onClose }: Props) {
  const dispute = useDisputeTimesheet();
  const [hours, setHours] = useState<string>(timesheet.hours_worked);
  const [notes, setNotes] = useState<string>("");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const errMsg = dispute.error
    ? dispute.error instanceof AxiosError
      ? (dispute.error.response?.data?.detail ?? "Couldn't submit dispute")
      : dispute.error.message
    : null;

  const hoursNum = Number(hours);
  const isHoursValid = !isNaN(hoursNum) && hoursNum >= 0 && hoursNum <= 24;
  const isNotesValid = notes.trim().length > 0;
  const canSubmit = isHoursValid && isNotesValid && !dispute.isPending;

  function submit() {
    if (!canSubmit) return;
    dispute.mutate(
      {
        id: timesheet.id,
        payload: { adjusted_hours: hoursNum, venue_notes: notes.trim() },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-copy">Dispute timesheet</h2>
        <p className="mt-1 text-sm text-copy-muted">
          The worker recorded {timesheet.hours_worked}h. Tell them what you'd like to change.
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor="adjusted-hours">Adjusted hours</Label>
            <Input
              id="adjusted-hours"
              type="number"
              min={0}
              max={24}
              step="0.25"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1"
            />
            {!isHoursValid && hours !== "" && (
              <p className="mt-1 text-xs text-destructive">Enter a number between 0 and 24.</p>
            )}
          </div>
          <div>
            <Label htmlFor="dispute-notes">Note to worker</Label>
            <Textarea
              id="dispute-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="e.g., Worker left 30 min early after closing."
              className="mt-1"
            />
            <p className="mt-1 text-xs text-copy-subtle">{notes.length} / 1000</p>
          </div>
        </div>

        {errMsg && <p className="mt-3 text-sm text-destructive">{errMsg}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={dispute.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {dispute.isPending ? "Submitting…" : "Submit dispute"}
          </Button>
        </div>
      </div>
    </div>
  );
}
